import type { ReactNode } from 'react'
import { useState } from 'react'
import {
  isCoachDraft,
  isPublishedCoachComment,
} from '@/lib/coaching/review-comment-visibility'
import { StatusPill } from '../shared/pills'
import { useCoachingStore } from '../store'
import type {
  PracticeSessionRow,
  ReviewPlanWeek,
  ReviewStatus,
  ReviewSubmission,
  ReviewType,
  ReviewWeekGroup,
  ReviewWeekStatus,
  SubmittedReviewRow,
} from './contracts'

/**
 * The Review tab's week list — "Awaiting review (N)" over one collapsible
 * section per plan week, newest first, with a single-expanded accordion over
 * the rows inside them.
 *
 * The sections come from the *plan*, not from the review queue (LG-646, ADR
 * 0013): every week the engagement ran through can appear, whether or not a
 * coach owes anything in it, and each row sits under the week it was **worked**
 * in. That is what makes a quiet week read as quiet instead of as absent, and
 * it is why there is no "Unscheduled" bucket and no separate "Recently
 * reviewed" list any more — every row has a week, and a reviewed row belongs
 * in the week it happened just as much as an unreviewed one.
 *
 * Owns the section mechanics + the collapsed/expanded card shells; the actual
 * expanded-review CONTENT (ranking banner, shared sections, per-type body,
 * gutter overlay) is supplied by the caller via `renderExpanded` —
 * review-tab.tsx wires that up (spec §8.1/§8.4) so this component stays
 * agnostic of the gutter-comment engine entirely.
 *
 * The accordion is single-expanded across the *whole* tab, not per section:
 * one review is what a coach reads at a time, and the sections say which week
 * that review belongs to.
 */

const TYPE_META: Record<
  ReviewType,
  { label: string; color: string; bg: string; border: string }
> = {
  coding: {
    label: 'Coding',
    color: 'var(--cd-amber)',
    bg: 'color-mix(in oklab, var(--cd-amber) 10%, transparent)',
    border: 'color-mix(in oklab, var(--cd-amber) 28%, transparent)',
  },
  sd: {
    label: 'System design',
    color: 'var(--cd-purple)',
    bg: 'color-mix(in oklab, var(--cd-purple) 10%, transparent)',
    border: 'color-mix(in oklab, var(--cd-purple) 25%, transparent)',
  },
  behavioral: {
    label: 'Behavioral',
    color: 'var(--cd-blue)',
    bg: 'color-mix(in oklab, var(--cd-blue) 10%, transparent)',
    border: 'color-mix(in oklab, var(--cd-blue) 28%, transparent)',
  },
  // The same grey the header's type strip already gives the `task` row.
  task: {
    label: 'Task',
    color: 'var(--cd-gray)',
    bg: 'color-mix(in oklab, var(--cd-gray) 10%, transparent)',
    border: 'color-mix(in oklab, var(--cd-gray) 28%, transparent)',
  },
}

function TypePill(props: { type: ReviewType }): ReactNode {
  const meta = TYPE_META[props.type]
  return (
    <span
      className="shrink-0 rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold"
      style={{
        color: meta.color,
        background: meta.bg,
        border: `1px solid ${meta.border}`,
      }}
    >
      {meta.label}
    </span>
  )
}

/** Nothing renders when the sprint is unknown — see `ReviewSubmission.sprint`. */
function SprintPill(props: { sprint?: string }): ReactNode {
  if (props.sprint === undefined) return null
  return (
    <span className="shrink-0 rounded-full border border-(--cd-border-2) bg-(--cd-inset) px-2.5 py-0.5 text-[11.5px] text-(--cd-text-muted)">
      {props.sprint}
    </span>
  )
}

/**
 * Lifecycle action for the review the coach is looking at. Which action is
 * offered follows the status the queue reports, and nothing here assumes what
 * the command does beyond the review itself: `finish` completes the assignment
 * under today's model and only flips the reviewed flag after the Completion
 * Redesign, and either way the queue is re-read afterwards.
 */
const LIFECYCLE_ACTION: Partial<
  Record<
    ReviewStatus,
    { label: string; pending: string; action: 'claim' | 'finish' | 'reopen' }
  >
> = {
  awaiting: { label: 'Claim review', pending: 'Claiming…', action: 'claim' },
  reopened: { label: 'Claim review', pending: 'Claiming…', action: 'claim' },
  in_review: {
    label: '✓ Finish review',
    pending: 'Finishing…',
    action: 'finish',
  },
  // Reopen moved here when the flat "Recently reviewed" list dissolved into
  // the weeks (LG-646): a finished review is now a normal row in the week it
  // was worked, so the action that undoes finishing has to live on the row
  // like every other lifecycle action rather than on a list of its own.
  reviewed: { label: 'Reopen', pending: 'Reopening…', action: 'reopen' },
  // `superseded` offers nothing: the card was cancelled, or — on the two
  // dialog-driven kinds, which still replace rather than accumulate — the
  // client handed the work in again. Either way nothing is owed on this row.
}

/**
 * What state a row is in, in the coach's words. Exactly one applies, so the
 * pill is the row's status and never a stack of them.
 *
 * `superseded` has two writers — a cancelled card, and a behavioral or
 * system-design resubmission, which still replaces the round before it — plus
 * the legacy coding rows LG-636 wrote before a re-solve stopped doing so. It
 * reads as "Superseded" rather than naming any of them: the row is here so the
 * week's story is complete, not because anything is owed on it.
 */
const REVIEW_STATE_PILL: Record<
  ReviewStatus,
  { label: string; tone: 'green' | 'red' | 'amber' | 'neutral' }
> = {
  awaiting: { label: 'Awaiting review', tone: 'amber' },
  in_review: { label: 'In review', tone: 'neutral' },
  reviewed: { label: '✓ Reviewed', tone: 'green' },
  reopened: { label: 'Reopened', tone: 'amber' },
  superseded: { label: 'Superseded', tone: 'neutral' },
}

/**
 * The row's one state. A session with no submission behind it says so — the
 * sixth state of the LG-646 row vocabulary, mutually exclusive with the five
 * review statuses because such a row has no review to be in any of them.
 * Nothing renders for a review-backed fixture row that carries neither.
 */
function ReviewStatePill(props: { submission: ReviewSubmission }): ReactNode {
  if (props.submission.notSubmitted === true) {
    return <StatusPill tone="neutral">Worked · not submitted</StatusPill>
  }
  const { reviewStatus } = props.submission
  if (reviewStatus === undefined) return null
  const meta = REVIEW_STATE_PILL[reviewStatus]
  return <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
}

/**
 * The `Unassigned` marker (spec D8 as amended, CONTEXT.md *Unassigned
 * practice*): work the client did without being asked, listed alongside the
 * assigned work — marked, not hidden. Orthogonal to the state pill, and
 * absent on the default assumption that work was assigned.
 */
function UnassignedBadge(props: { submission: ReviewSubmission }): ReactNode {
  if (props.submission.unassigned !== true) return null
  return (
    <span
      className="shrink-0 rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold"
      style={{
        color: 'var(--cd-purple)',
        background: 'color-mix(in oklab, var(--cd-purple) 10%, transparent)',
        border:
          '1px solid color-mix(in oklab, var(--cd-purple) 35%, transparent)',
      }}
    >
      Unassigned
    </span>
  )
}

/**
 * The `Pulled in` marker (LG-647, spec D8): this row's assignment was created
 * by the coach *after* the client did the work, so that it could be reviewed
 * at all.
 *
 * Rendered beside `Unassigned` rather than instead of it, and both are true:
 * the client still did this work unasked, and the card behind it is still not
 * one anybody planned. Collapsing the two would let a retroactive card read
 * as a week's homework.
 */
function PulledInBadge(props: { submission: ReviewSubmission }): ReactNode {
  if (props.submission.pulledIn !== true) return null
  return (
    <span className="shrink-0 rounded-full border border-(--cd-border-3) bg-(--cd-inset) px-2.5 py-0.5 text-[11.5px] font-semibold text-(--cd-text-2)">
      Pulled in
    </span>
  )
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * `Mar 3–9`, or `Mar 30 – Apr 5` across a month boundary.
 *
 * Formatted straight off the `YYYY-MM-DD` keys rather than through a `Date`:
 * these name days, not instants, and a date-only value rendered in the
 * browser's zone shows the day before for anyone west of UTC — which on a week
 * header is a whole week labelled wrong.
 */
function weekDateRange(week: ReviewPlanWeek): string {
  const [, startMonth = '', startDay = ''] = week.startsOnKey.split('-')
  const [, endMonth = '', endDay = ''] = week.endsOnKey.split('-')
  const month = (value: string): string => MONTHS[Number(value) - 1] ?? value
  const day = (value: string): string => String(Number(value))
  return startMonth === endMonth
    ? `${month(startMonth)} ${day(startDay)}–${day(endDay)}`
    : `${month(startMonth)} ${day(startDay)} – ${month(endMonth)} ${day(endDay)}`
}

/**
 * The week's place in the publication lifecycle — whether the client was ever
 * given this week's homework, which is a different question from whether they
 * did anything in it. A draft week with work in it is exactly the case this
 * tab exists to surface: they practised without being asked.
 */
const WEEK_STATUS_PILL: Record<
  ReviewWeekStatus,
  { label: string; tone: 'green' | 'red' | 'amber' | 'neutral' }
> = {
  draft: { label: 'Draft', tone: 'neutral' },
  published: { label: 'Published', tone: 'green' },
  closed: { label: 'Closed', tone: 'neutral' },
}

function LifecycleButton(props: {
  label: string
  pendingLabel: string
  /** Renders the button held open (disabled) without a pending state. */
  held?: boolean
  onRun: () => Promise<void>
}): ReactNode {
  const [pending, setPending] = useState(false)

  return (
    <button
      type="button"
      disabled={pending || props.held === true}
      onClick={async () => {
        setPending(true)
        try {
          await props.onRun()
        } finally {
          setPending(false)
        }
      }}
      className="rounded-md border border-(--cd-border-3) px-3 py-1 text-[12px] font-semibold text-(--cd-text) disabled:opacity-50"
    >
      {pending ? props.pendingLabel : props.label}
    </button>
  )
}

/**
 * Unreleased coach work on one submission (LG-641). Counted off the
 * submission's own comments — the same list the cards render — so the
 * marking cannot drift from what publishing would actually move.
 */
function draftCount(submission: SubmittedReviewRow): number {
  return submission.comments.filter(isCoachDraft).length
}

function publishedCount(submission: SubmittedReviewRow): number {
  return submission.comments.filter(isPublishedCoachComment).length
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

/**
 * What the confirmation is allowed to say about the client having read this.
 *
 * Three states, and the middle one is not "never": only a view taken while
 * some of what is about to be withdrawn was visible counts, so a client who
 * read an older batch and never came back reads as not having opened *this*
 * feedback (LG-641). `undefined` is nothing tracking it at all, which is a
 * silence rather than a claim.
 *
 * Phrased from the reading the slice is about to acknowledge — not from this
 * row's page-load snapshot — so the sentence the coach agrees to is the one
 * the write insists on.
 */
function viewedSentence(
  clientLastViewedWhen: string | null | undefined,
): string {
  if (clientLastViewedWhen === undefined) return ''
  return clientLastViewedWhen === null
    ? ' The client has not opened this feedback since it was published.'
    : ` The client last opened this feedback ${clientLastViewedWhen}.`
}

function DraftBadge(props: { submission: SubmittedReviewRow }): ReactNode {
  // A practice row has no review to hold drafts — the prop type says so, and
  // the card shells narrow before rendering any review chrome.
  const count = draftCount(props.submission)
  if (count === 0) return null
  return (
    <span
      className="shrink-0 rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold"
      style={{
        color: 'var(--cd-amber)',
        background: 'color-mix(in oklab, var(--cd-amber) 10%, transparent)',
        border:
          '1px solid color-mix(in oklab, var(--cd-amber) 40%, transparent)',
      }}
    >
      {plural(count, 'unpublished draft')}
    </span>
  )
}

/**
 * The publish control, at the top of the review (LG-641). Publishing is the
 * only thing that puts coach feedback in front of the client — Finish is a
 * queue-state action and moves nothing — so it is stated here, above the
 * work, rather than folded into the lifecycle footer.
 *
 * Unpublishing is confirmed first, and the confirmation says when the client
 * last opened the feedback: an unpublish they have already read cannot be
 * pretended away.
 */
function PublicationBar(props: { submission: SubmittedReviewRow }): ReactNode {
  const { submission } = props
  const { publish, unpublish } = useCoachingStore().review
  const drafts = draftCount(submission)
  const published = publishedCount(submission)
  if (drafts === 0 && published === 0) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-(--cd-border-2) bg-(--cd-inset) px-3.5 py-2.5">
      <p className="text-[12.5px] text-(--cd-text-2)">
        {drafts === 0
          ? `${plural(published, 'comment')} published — the client can read this feedback.`
          : `${plural(drafts, 'draft')} not published — the client cannot see ${drafts === 1 ? 'it' : 'them'} yet.`}
      </p>
      <div className="flex shrink-0 items-center gap-2">
        {published > 0 && (
          <LifecycleButton
            label="Unpublish"
            pendingLabel="Unpublishing…"
            onRun={() =>
              unpublish(submission.id, (clientLastViewedWhen) =>
                window.confirm(
                  `Unpublish ${plural(published, 'published comment')}? The client loses access to ${published === 1 ? 'it' : 'them'} until you publish again.${viewedSentence(clientLastViewedWhen)}`,
                ),
              )
            }
          />
        )}
        {drafts > 0 && (
          <LifecycleButton
            label={`Publish ${plural(drafts, 'draft')}`}
            pendingLabel="Publishing…"
            onRun={() => publish(submission.id)}
          />
        )}
      </div>
    </div>
  )
}

function ExtraChips(props: { chips: string[] }): ReactNode {
  return (
    <>
      {props.chips.map((chip) => (
        <span
          key={chip}
          className="shrink-0 rounded-[7px] border border-(--cd-border-2) bg-(--cd-inset) px-2.5 py-0.5 text-xs text-(--cd-text-2)"
        >
          {chip}
        </span>
      ))}
    </>
  )
}

function CollapsedCard(props: {
  submission: ReviewSubmission
  onExpand: () => void
}): ReactNode {
  const { submission, onExpand } = props
  // The one branch on the row union (LG-646): a practice row wears no review
  // chrome, and past this line the compiler enforces it.
  const review = submission.notSubmitted === true ? null : submission
  // The unreleased-work border, on the collapsed shell too: the coach must be
  // able to tell without opening anything (LG-641).
  const hasDrafts = review !== null && draftCount(review) > 0
  return (
    <button
      type="button"
      onClick={onExpand}
      className={`flex items-center justify-between gap-3 rounded-2xl border bg-(--cd-card) px-5 py-4 text-left ${
        hasDrafts
          ? 'border-(--cd-amber)'
          : 'border-(--cd-border) hover:border-(--cd-border-3)'
      }`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[15px] font-semibold text-(--cd-text)">
          {submission.title}
        </span>
        <ReviewStatePill submission={submission} />
        <UnassignedBadge submission={submission} />
        <PulledInBadge submission={submission} />
        <SprintPill sprint={submission.sprint} />
        <TypePill type={submission.type} />
        {review !== null && <DraftBadge submission={review} />}
        <ExtraChips chips={submission.chips} />
      </div>
      <span className="shrink-0 text-[12.5px] text-(--cd-text-faint)">
        {submission.submittedWhen}
      </span>
    </button>
  )
}

function LifecycleFooter(props: { submission: SubmittedReviewRow }): ReactNode {
  const { claim, finish, reopen } = useCoachingStore().review
  const status = props.submission.reviewStatus
  const entry = status === undefined ? undefined : LIFECYCLE_ACTION[status]
  if (entry === undefined) return null
  // Only Finish is a sign-off; claiming a review with parts still loading is
  // how the coach gets it in front of them in the first place.
  const heldReason =
    entry.action === 'finish'
      ? props.submission.finishDisabledReason
      : undefined
  // Finishing publishes nothing (LG-641). It is a queue-state action, so a
  // review with drafts still in it can be finished — but not silently: the
  // coach is told what stays invisible and has to agree to it.
  //
  // Live on coding rows, which is the tab's own primary type: LG-517 already
  // retired the seed-artifact hold there, so a coach with a draft still open
  // meets this confirmation in production. System design and behavioral rows
  // keep Finish held until their bodies render the client's submission, and
  // reach it only once that hold goes.
  const drafts = entry.action === 'finish' ? draftCount(props.submission) : 0

  return (
    <div className="flex flex-col items-end gap-2">
      {drafts > 0 && (
        <p className="text-[12px] text-(--cd-amber)">
          Finishing does not publish anything — {plural(drafts, 'draft')} would
          stay invisible to the client.
        </p>
      )}
      <div className="flex items-center justify-end gap-3">
        {heldReason !== undefined && (
          <p className="text-[12px] text-(--cd-text-muted)">{heldReason}</p>
        )}
        <LifecycleButton
          label={entry.label}
          pendingLabel={entry.pending}
          held={heldReason !== undefined}
          onRun={async () => {
            if (entry.action === 'claim') {
              await claim(props.submission.id)
              return
            }
            if (entry.action === 'reopen') {
              await reopen(props.submission.id)
              return
            }
            if (
              drafts > 0 &&
              !window.confirm(
                `This review still has ${plural(drafts, 'unpublished draft')}. Finishing does not publish ${drafts === 1 ? 'it' : 'them'} — the client will never see ${drafts === 1 ? 'it' : 'them'} unless you publish. Finish anyway?`,
              )
            ) {
              return
            }
            await finish(props.submission.id)
          }}
        />
      </div>
    </div>
  )
}

/**
 * The one action a practice row offers (LG-647, spec D8): pull this work into
 * the week so there is something to comment on.
 *
 * Only on rows the mapper marked `pullable` — unassigned work that already
 * qualified. Everything else has no review to produce, and a button that
 * cannot produce one would be a promise the server has to break.
 *
 * The sentence beside it is not decoration: this write creates an assignment
 * on the client's plan, which is not what "leave a comment" sounds like, and
 * the coach should know before clicking rather than after.
 */
function PracticeFooter(props: { submission: PracticeSessionRow }): ReactNode {
  const { pull } = useCoachingStore().review
  if (props.submission.pullable !== true) return null
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-[12px] text-(--cd-text-muted)">
        Pulling this in adds it to the client's plan for this week as work they
        have already finished, so you can review it.
      </p>
      <LifecycleButton
        label="Pull into this week"
        pendingLabel="Pulling in…"
        onRun={() => pull(props.submission.id)}
      />
    </div>
  )
}

function ExpandedCard(props: {
  submission: ReviewSubmission
  children: ReactNode
}): ReactNode {
  const { submission, children } = props
  // The same one branch as the collapsed shell, taken both ways: everything
  // review-only below renders off `review` and everything practice-only off
  // `practice`, so neither arm can reach the other's chrome.
  const review = submission.notSubmitted === true ? null : submission
  const practice = submission.notSubmitted === true ? submission : null
  const hasDrafts = review !== null && draftCount(review) > 0
  return (
    <div
      className={`flex flex-col gap-3.5 rounded-2xl border bg-(--cd-shell) p-5 ${
        hasDrafts ? 'border-(--cd-amber)' : 'border-(--cd-border-3)'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[17px] font-bold text-(--cd-text)">
              {submission.title}
            </span>
            <ReviewStatePill submission={submission} />
            <UnassignedBadge submission={submission} />
            <PulledInBadge submission={submission} />
            <SprintPill sprint={submission.sprint} />
            <TypePill type={submission.type} />
            {review !== null && <DraftBadge submission={review} />}
          </div>
          <div className="flex flex-wrap gap-2">
            <ExtraChips chips={submission.chips} />
          </div>
        </div>
        <span className="shrink-0 text-[12.5px] text-(--cd-text-faint)">
          {submission.submittedWhen}
        </span>
      </div>
      {review !== null && <PublicationBar submission={review} />}
      {children}
      {review !== null && <LifecycleFooter submission={review} />}
      {practice !== null && <PracticeFooter submission={practice} />}
    </div>
  )
}

/**
 * One week's section: the header that folds it, and the rows inside.
 *
 * The header says two numbers, obligation first: what the coach still owes for
 * this week, then how much work it holds altogether. Both are counted
 * server-side over the whole week, so they are true before the rows are read —
 * which matters, because a folded week's rows are never read at all.
 *
 * The rows themselves arrive when the section opens (LG-646). Three states
 * have to stay distinguishable and none may masquerade as another: still
 * loading, could not be read, and genuinely nothing — that last one being the
 * fact this whole change exists to make visible.
 */
function WeekSection(props: {
  group: ReviewWeekGroup
  collapsed: boolean
  onToggle: () => void
  children: ReactNode
}): ReactNode {
  const { group, collapsed, onToggle } = props
  const { week } = group
  const status = WEEK_STATUS_PILL[week.status]

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex items-center justify-between gap-3 rounded-xl border border-(--cd-border) bg-(--cd-inset) px-4 py-2.5 text-left hover:border-(--cd-border-3)"
      >
        <span className="flex flex-wrap items-center gap-2">
          <span
            aria-hidden="true"
            className="text-[11px] text-(--cd-text-faint)"
          >
            {collapsed ? '▸' : '▾'}
          </span>
          <span className="text-[13px] font-semibold text-(--cd-text)">
            Week {week.number}
          </span>
          <span className="text-[12px] text-(--cd-text-muted)">
            {weekDateRange(week)}
          </span>
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        </span>
        <span className="shrink-0 text-[12px] text-(--cd-text-muted)">
          {week.awaitingCount} awaiting review · {week.workedCount} worked
        </span>
      </button>

      {!collapsed && (
        <>
          {group.error !== null && (
            <p role="alert" className="text-[13px] text-(--cd-red)">
              {group.error}
            </p>
          )}
          {group.error === null && group.isLoading && (
            <p role="status" className="text-[13px] text-(--cd-text-muted)">
              Loading this week's work…
            </p>
          )}
          {/* Quiet, not capped: "nothing" is only true when the read came back
              whole, so the truncation notice below rules it out. */}
          {group.error === null &&
            !group.isLoading &&
            !group.isTruncated &&
            group.submissions.length === 0 && (
              <p className="text-[13px] text-(--cd-text-muted)">
                Nothing worked this week.
              </p>
            )}
          {/* A capped read says so. The header's count comes from a separate,
              uncapped query, so without this the section would simply show
              fewer rows than it claims and look like a miscount. */}
          {group.isTruncated && (
            <p role="status" className="text-[13px] text-(--cd-amber)">
              Showing the first {group.submissions.length} of this week's work.
            </p>
          )}
          {props.children}
        </>
      )}
    </div>
  )
}

export function ReviewQueue(props: {
  renderExpanded: (submission: ReviewSubmission) => ReactNode
}): ReactNode {
  const { renderExpanded } = props
  const { review } = useCoachingStore()
  const {
    weeks,
    expandedId,
    setExpandedId,
    isWeekCollapsed,
    toggleWeekCollapsed,
    awaitingCount,
    status,
  } = review

  return (
    <div className="flex flex-col gap-3">
      <div className="text-[13px] font-semibold text-(--cd-text-2)">
        Awaiting review{' '}
        <span className="text-(--cd-text-faint)">({awaitingCount})</span>
      </div>

      {/* The weeks are what the coach came for: say when the spine is still
          loading or could not be read, rather than showing no sections as if
          the engagement had never run. */}
      {status.error !== null && (
        <p role="alert" className="text-[13px] text-(--cd-red)">
          {status.error}
        </p>
      )}
      {status.error === null && status.isLoading && (
        <p role="status" className="text-[13px] text-(--cd-text-muted)">
          Loading the review weeks…
        </p>
      )}
      {/* Worded for the condition that actually produced it. An empty spine
          means the *plan* has no weeks the tab can section by — not that the
          client has done nothing, which is a claim this branch cannot make. A
          client with weeks and no submissions gets those weeks, each saying
          "Nothing worked this week." */}
      {status.error === null && !status.isLoading && weeks.length === 0 && (
        <p className="text-[13px] text-(--cd-text-muted)">
          This client's plan has no weeks yet.
        </p>
      )}

      {weeks.map((group) => (
        <WeekSection
          key={group.week.id}
          group={group}
          collapsed={isWeekCollapsed(group.week.id)}
          // Just the fold: the lane state owns what folding does to the
          // open review (`useWeekFolds` + `useReviewUiState`), so this
          // cannot get it wrong.
          onToggle={() => toggleWeekCollapsed(group.week.id)}
        >
          {group.submissions.map((submission) =>
            submission.id === expandedId ? (
              // Keyed by submission id (== expandedId in this branch): the
              // mandatory guard so React fully unmounts a submission's
              // comment tree/composer/OverallComment when the accordion
              // switches to a different submission, rather than diff-reusing
              // the subtree — `post()` targets the slice's `expandedId`, so a
              // stale/reused subtree could otherwise post a comment onto the
              // wrong submission.
              <ExpandedCard key={submission.id} submission={submission}>
                {renderExpanded(submission)}
              </ExpandedCard>
            ) : (
              <CollapsedCard
                key={submission.id}
                submission={submission}
                onExpand={() => setExpandedId(submission.id)}
              />
            ),
          )}
        </WeekSection>
      ))}
    </div>
  )
}
